import { useSession } from "next-auth/react";
import { type RouterOutput } from "~/server/api/root";

import { format } from "date-fns";
import { srLatn } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import {
  PaymentConfirmationComponent,
  PaymentConfirmationLoadingShell,
} from "../payment-confirmation";
import { useParams } from "next/navigation";
import { api } from "~/trpc/react";
import { useEffect, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { TherapyRelationshipType } from "~/prisma/browser";
import { useDiscount } from "~/hooks/use-discount";
import { useQueryState } from "nuqs";
import {
  parseAsIsoDateTime,
  parseAsStringEnum,
  parseAsInteger,
  parseAsBoolean,
} from "nuqs";
import { getClientDisplayName } from "~/lib/client-display";

interface SessionPaymentProps {
  therapist: NonNullable<RouterOutput["therapistRouter"]["getTherapist"]>;
  attributionBridgeId?: string;
}

export function getPaymentFailureMessage(paymentCaptureUncertain: boolean) {
  return paymentCaptureUncertain
    ? "Ne možemo da potvrdimo status tvog PayPal plaćanja. Nemoj pokušavati ponovo dok ne proveriš stanje u PayPal-u ili se ne javiš podršci."
    : "Ništa ti nije naplaćeno. Pokušaj ponovo ili izaberi drugi način plaćanja.";
}

export function getCheckoutLoadingDateTime(
  sessionDate: Date | null,
  clientTimezone: string | null,
) {
  if (!sessionDate || !clientTimezone) return "Izabrani termin";
  return formatInTimeZone(sessionDate, clientTimezone, "PPp", {
    locale: srLatn,
  });
}

export default function StepPayment(props: SessionPaymentProps) {
  const session = useSession();
  const [sessionDate] = useQueryState("date", parseAsIsoDateTime);
  const [therapyType] = useQueryState(
    "therapyType",
    parseAsStringEnum<TherapyRelationshipType>(
      Object.values(TherapyRelationshipType),
    ).withDefault(TherapyRelationshipType.INDIVIDUAL),
  );
  const [step, setStepQuery] = useQueryState(
    "step",
    parseAsInteger.withDefault(2),
  );
  const [proposalId] = useQueryState("proposalId", parseAsInteger);
  const [paymentFailed] = useQueryState(
    "paymentFailed",
    parseAsBoolean.withDefault(false),
  );
  const [paymentCaptureUncertain] = useQueryState(
    "paymentCaptureUncertain",
    parseAsBoolean.withDefault(false),
  );
  const [clientTimezone, setClientTimezone] = useState<string | null>(null);

  useEffect(() => {
    setClientTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, []);

  const { calculateDiscountedPrice } = useDiscount();

  // If sessionTimestamp is missing or invalid, redirect to step 0
  useEffect(() => {
    if (!sessionDate) {
      void setStepQuery(0);
    }
  }, [sessionDate, setStepQuery]);

  const params = useParams<{ therapistSlug: string }>();

  const therapistSlug = params.therapistSlug;

  const currentRelationships =
    api.therapySessionRouter.getTherapyRelationships.useQuery(undefined, {
      enabled: session.data?.user.role === "PATIENT",
    });

  const isAlreadyInRelationship = currentRelationships.data?.find(
    (relationship) => relationship.therapist.slug === therapistSlug,
  );

  const getPrice = () => {
    const regularPrice = isAlreadyInRelationship
      ? isAlreadyInRelationship.price
      : props.therapist.defaultPrice;

    let basePrice = regularPrice;

    if (therapyType === TherapyRelationshipType.COUPLES) {
      if (!props.therapist.couplesTherapyPrice) {
        basePrice = regularPrice;
      } else {
        basePrice =
          isAlreadyInRelationship?.type === TherapyRelationshipType.COUPLES
            ? isAlreadyInRelationship.price
            : props.therapist.couplesTherapyPrice;
      }
    }

    const originalPrice = basePrice;

    // Apply discount if available
    return {
      price: calculateDiscountedPrice(basePrice),
      originalPrice,
    };
  };

  useEffect(() => {
    if (session.status !== "loading" && session.status !== "authenticated") {
      void setStepQuery(1);
    }
  }, [session, setStepQuery]);

  if (
    session.status === "loading" ||
    currentRelationships.isLoading ||
    !sessionDate
  ) {
    return (
      <PaymentConfirmationLoadingShell
        therapyType={therapyType}
        therapistName={props.therapist.user.name ?? ""}
        therapistImage={props.therapist.user.image ?? undefined}
        formattedDateTime={getCheckoutLoadingDateTime(
          sessionDate,
          clientTimezone,
        )}
        sessionDurationMinutes={
          therapyType === TherapyRelationshipType.COUPLES
            ? props.therapist.couplesSessionDuration
            : props.therapist.individualSessionDuration
        }
      />
    );
  }

  if (session.status === "unauthenticated") {
    return (
      <Alert variant="destructive" className="mx-auto max-w-md">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Prijavi se da bi nastavio/la sa plaćanjem. Preusmeravamo te na
          prijavu.
        </AlertDescription>
      </Alert>
    );
  }

  const price = getPrice();
  const sessionTimestamp = sessionDate.toISOString();
  const checkoutKey = `${therapyType}:${sessionTimestamp}`;

  return (
    <div className="space-y-4">
      {paymentFailed && (
        <Alert variant="destructive" className="mx-auto max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Plaćanje nije uspelo</AlertTitle>
          <AlertDescription>
            {getPaymentFailureMessage(paymentCaptureUncertain)}
          </AlertDescription>
        </Alert>
      )}
      <PaymentConfirmationComponent
        key={checkoutKey}
        therapyType={therapyType}
        clientName={getClientDisplayName({ name: session.data?.user.name })}
        therapistName={props.therapist.user.name ?? ""}
        therapistImage={props.therapist.user.image ?? undefined}
        sessionDate={sessionDate ? format(sessionDate, "dd/MM/yyyy") : ""}
        sessionTime={sessionDate ? format(sessionDate, "HH:mm") : ""}
        sessionCost={price.originalPrice}
        sessionDurationMinutes={
          therapyType === TherapyRelationshipType.COUPLES
            ? props.therapist.couplesSessionDuration
            : props.therapist.individualSessionDuration
        }
        paymentInfo={{
          therapistId: props.therapist.id,
          sessionTimestamp,
          attributionBridgeId: props.attributionBridgeId,
          proposalMessageId: proposalId ?? undefined,
        }}
      />
    </div>
  );
}
